import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import io from "socket.io-client";
import * as config from "../../config.js";
import { getLiveEvent, endLiveEventOnServer } from "../../services/liveevents";
import Select from "react-select";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Swal from "sweetalert2";
import LiveEventChatSession from "components/LiveEvent/LiveEventChatSession";
import TabBar from "components/TabBar";
import Audience from "components/LiveEvent/Audience";
import Approved from "components/LiveEvent/Approved";
import Incoming from "components/LiveEvent/Incoming";
import Answered from "components/LiveEvent/Answered";
import Unreviewed from "components/LiveEvent/Unreviewed";
import Flagged from "components/LiveEvent/Flagged";
import { Button } from "components/LiveEvent/Elements";
import Loading from "components/Loading";
import {
  getAgentAnswer,
  saveVideoToLiveEvent,
  saveLiveEvent,
} from "services/liveevents";
import { AppContext } from "contexts/AppContext";
import { sendGroupNotification } from "services/users";
import {
  getFullTimeZone,
  watchHlsSimple,
  blobToBase64,
} from "../../utils/utils";


const AnswerModal = NiceModal.create(({ question, position, event_id }) => {
  const { activeUser } = useSelector((state) => state.users);
  const [error, setError] = useState();
  const [agentAnswering, setAgentAnswering] = useState(false);
  const answerRef = useRef(null);
  const modal = useModal();
  return (
    <div
      className="w-5/12 h-auto force-height-90 items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none"
      style={{
        position: "fixed",
        top: position?.top || "50%",
        left: position?.left || "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "10px",
          maxWidth: "500px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>Answer Question</h2>
          <Button
            onClick={async () => {
              setError(null);
              setAgentAnswering(true);
              const response = await getAgentAnswer(
                event_id,
                question?.message
              );

              if (!response.success) {
                setError(response.message);
                return;
              }

              const answer = response.answer;
              if (answer?.agent_message?.message) {
                // If the agent message is structured, use it
                question.answer = answer.agent_message.message;
                answerRef.current.value = question.answer;
                modal.resolve({
                  response: question.answer,
                  source: "agent",
                });
                modal.remove();
              } else {
                Swal.fire({
                  title: "Error",
                  text: "Failed to get agent answer. Please try again.",
                  icon: "error",
                });
              }
              setAgentAnswering(false);
            }}
          >
            <i className="fas fa-robot"></i>
            {agentAnswering ? "Thinking..." : "Agent Answer"}
          </Button>
        </div>
        <p>{question?.message}</p>
        <textarea
          ref={answerRef}
          rows="4"
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            marginBottom: "10px",
          }}
          onChange={(e) => {
            question.answer = e.target.value;
          }}
          placeholder="Type your answer here..."
        ></textarea>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          {!agentAnswering && (
            <Button
              bgcolor="#4CAF50"
              textcolor="#fff"
              onClick={() => {
                setError(null);
                // Handle answer submission logic here
                console.log("Answer submitted for question:", question);
                modal.resolve({
                  response: question.answer,
                  source: `${activeUser?.firstname} ${activeUser?.lastname}`,
                });
                modal.remove();
              }}
            >
              Submit Answer
            </Button>
          )}
          <Button
            bgcolor="#f44336"
            textcolor="#fff"
            onClick={() => modal.remove()}
          >
            Cancel
          </Button>
        </div>
        {error && (
          <div
            style={{
              color: "red",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

NiceModal.register("answer-modal", AnswerModal);

const MessageModal = NiceModal.create(({ position }) => {
  const modal = useModal();
  const [message, setMessage] = useState("");
  return (
    <div
      className="w-5/12 h-auto force-height-90 items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none"
      style={{
        position: "fixed",
        top: position?.top || "50%",
        left: position?.left || "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "10px",
          maxWidth: "500px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>Send Message</h2>
        </div>
        <textarea
          rows="4"
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            marginBottom: "10px",
          }}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          placeholder="Type your message here..."
        ></textarea>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            bgcolor="#4CAF50"
            textcolor="#fff"
            onClick={() => {
              // Handle answer submission logic here
              modal.resolve(message);
              modal.remove();
            }}
          >
            Send Message
          </Button>
          <Button
            bgcolor="#f44336"
            textcolor="#fff"
            onClick={() => modal.remove()}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
});

NiceModal.register("message-modal", MessageModal);

const NotificationModal = NiceModal.create(({ position, allUsers }) => {
  const { selectedClient } = useContext(AppContext);
  const modal = useModal();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendingNotication, setSendingNotication] = useState(false);
  return (
    <div
      className="w-5/12 h-auto force-height-90 items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none"
      style={{
        position: "fixed",
        top: position?.top || "50%",
        left: position?.left || "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "10px",
          maxWidth: "500px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>Send Notification</h2>
        </div>
        <input
          type="text"
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            marginBottom: "10px",
          }}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="Notification Title"
        />
        <textarea
          rows="4"
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            marginBottom: "10px",
          }}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          placeholder="Type your message here..."
        ></textarea>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            bgcolor="#4CAF50"
            textcolor="#fff"
            onClick={async () => {
              if (!sendingNotication) {
                setSendingNotication(true);
                const userIds = allUsers.map((user) => user.id).join(", ");
                const clientId = selectedClient?.id;
                console.log(
                  "!!! NOTIFICATION SENT: ",
                  title,
                  message,
                  userIds,
                  clientId
                );
                const results = await sendGroupNotification(
                  clientId,
                  userIds,
                  title,
                  message
                );
                if (results.success) {
                  Swal.fire({
                    title: "Success",
                    text: "Notifications sent successfully",
                    icon: "success",
                    button: "OK",
                    confirmButtonText: "OK",
                  });
                } else {
                  Swal.fire({
                    title: "Error",
                    text: results.message,
                    icon: "error",
                    button: "OK",
                    confirmButtonText: "OK",
                  });
                }
                modal.remove();
                setSendingNotication(false);
              }
            }}
          >
            {sendingNotication ? "Sending..." : "Send Notification"}
          </Button>
          <Button
            bgcolor="#f44336"
            textcolor="#fff"
            onClick={() => modal.remove()}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
});

NiceModal.register("notification-modal", NotificationModal);

export const LiveEventDetails = () => {
  const { activeUser, token } = useSelector((state) => state.users);

  const { event_id } = useParams();
  const [recording, setRecording] = useState(false);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [liveEvent, setLiveEvent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [approvedCount, setApprovedCount] = useState(0);
  const [incomingCount, setIncomingCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [flaggedCommentsCount, setFlaggedCommentsCount] = useState(0);
  const [unreviewedCount, setUnreviewedCount] = useState(0);
  const [unreviewedCommentsCount, setUnreviewedCommentsCount] = useState(0);
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [selectedSidebar, setSelectedSidebar] = useState("Chat");
  const [selectedBottomBar, setSelectedBottomBar] = useState("Incoming");
  const [selectedDisplay, setSelectedDisplay] = useState(
    window.location.hash
      ? window.location.hash.replace("#", "").replace(/-/g, " ")
      : "presenter and moderator"
  );
  const [showModeratorVideo, setShowModeratorVideo] = useState(false);
  const [sidebarBottomHeight, setSidebarBottomHeight] = useState(0);
  const [bottomHeight, setBottomHeight] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  const [selectedVideoURL, setSelectedVideoURL] = useState(false);
  const [scheduledVideoOptions, setScheduledVideoOptions] = useState([]);

  const preferred = [
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9,opus",
    "video/webm",
  ];
  const mimeType =
    preferred.find((t) => MediaRecorder.isTypeSupported(t)) || "";

  const displayOptions = [
    { value: "presenter and moderator", label: "Presenter and Moderator" },
    { value: "presenter", label: "Presenter" },
    { value: "moderator", label: "Moderator" },
  ];

  const socketRef = useRef(null);
  const streamingSocketRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunks = useRef([]);
  const recognitionRef = useRef(null);
  const videoElementRef = useRef(null);
  const streamingStatusRef = useRef(false);
  const startedHLSStreamRef = useRef(false);
  const recordedBlob = useRef(null);

  useEffect(() => {
    console.log("!!! QUESTIONS UPDATED: ", questions);
  }, [questions]);

  useEffect(() => {
    console.log("!! UPDATE DISPLAY: ", selectedDisplay);
    if (!selectedDisplay.includes("moderator")) {
      setSelectedSidebar("Chat");
      setSelectedBottomBar("Incoming");
    } else if (selectedDisplay === "moderator") {
      if (liveEvent?.status === "streaming") {
        streamingStatusRef.current = "streaming";
        startHLSStream(liveEvent);
      }
    }

    if (selectedDisplay.includes("presenter")) {
      stopHLSStream();
    }

    // Set the hash on the url
    const hash = selectedDisplay.replace(/\s+/g, "-").toLowerCase();
    window.history.replaceState(
      null,
      null,
      window.location.pathname + `#${hash}`
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDisplay]);

  const updateBottomHeight = () => {
    if (videoElementRef.current) {
      const rect = videoElementRef.current.getBoundingClientRect();
      const bottom = rect.bottom;
      const remaining = window.innerHeight - bottom;
      console.log("!!! REMAINING HEIGHT: ", remaining);
      setBottomHeight(Math.max(0, remaining - 80));
      setSidebarBottomHeight(Math.max(0, window.innerHeight - 60));
    }
  };

  useEffect(() => {
    const handleResize = () => {
      clearTimeout(window._resizeTimeout);
      window._resizeTimeout = setTimeout(updateBottomHeight, 100); // debounce
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("load", updateBottomHeight);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", updateBottomHeight);
    };
  }, []);

  useEffect(() => {
    setApprovedCount(
      questions.filter(
        (q) => q.status === "approved" || q.status === "answered" || q.response
      ).length
    );
    setIncomingCount(
      questions.filter((q) => !q.inappropriate && q.status === "pending").length
    );
    setAnsweredCount(
      questions.filter((q) => q.status === "answered" || q.response).length
    );
    setFlaggedCount(questions.filter((q) => q.inappropriate).length);
    setUnreviewedCount(
      questions.filter((q) => !q.status || q.status === "unreviewed").length
    );
    updateBottomHeight();
  }, [questions]);

  useEffect(() => {
    setFlaggedCommentsCount(comments.filter((c) => c.inappropriate).length);
    setUnreviewedCommentsCount(
      comments.filter((c) => !c.status || c.status === "unreviewed").length
    );
    updateBottomHeight();
  }, [comments]);

  useEffect(() => {
    console.log("!!! SELECTED VIDEO URL: ", selectedVideoURL);
    if (videoElementRef.current) {
      videoElementRef.current.src = selectedVideoURL;
      videoElementRef.current.autoplay = true;
    }
  }, [selectedVideoURL]);

  useEffect(() => {
    if (!liveEvent) {
      return;
    }

    if (liveEvent.event_type === "scheduled" && liveEvent.scheduled_videos) {
      const options = [];
      liveEvent.scheduled_videos.forEach((v) => {
        options.push({
          value: v.video_url,
          label: v.title || "No title",
        });
      });
      setScheduledVideoOptions(options);
    }

    if (liveEvent.video_url && videoElementRef.current) {
      videoElementRef.current.src = liveEvent.video_url;
      videoElementRef.current.autoplay = false;
      setSelectedVideoURL(liveEvent.video_url);
    }

    console.log(
      "!!! LIVE EVENT STATUS: ",
      liveEvent?.status,
      liveEvent?.completed_at,
      liveEvent?.messages.length
    );
    if (hasEventEnded()) {
      updateBottomHeight();
      return;
    }

    if (!socketRef.current) {
      console.log(
        "!!!! CONNECTING WEBSOCKETS: ",
        config.WEBSOCKET_SERVER_URL,
        config.WEBSOCKET_PATH
      );
      /*
      socketRef.current = io(`${config.WEBSOCKET_SERVER_URL}`, {
        path: config.WEBSOCKET_PATH,
        transports: ["websocket"],
        query: {
          access_key: config.WEBSOCKET_ACCESS_KEY,
        },
      });
      */
      console.log(
        "!!! ATTEMPTING TO CONNECT TO WEBSOCKET SERVER: ",
        config.WEBSOCKET_SERVER_URL
      );
      socketRef.current = io(config.WEBSOCKET_SERVER_URL, {
        path: config.WEBSOCKET_PATH,
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 8000,
        randomizationFactor: 0.5,
        forceNew: false,
      });

      // baseline debug to see WHY it reconnects
      /*socketRef.current.on("connect", () =>
        console.log("connect", socketRef.current?.id)
      );*/
      socketRef.current.on("disconnect", (r) => console.log("disconnect:", r));
      socketRef.current.on("reconnect", (n) => console.log("reconnect", n));
      socketRef.current.on("reconnect_attempt", () =>
        console.log("reconnect_attempt")
      );
      socketRef.current.on("connect_error", (e) =>
        console.log("connect_error:", e?.message || e)
      );

      // server events
      socketRef.current.on("news", (x) => console.log("news", x));
      socketRef.current.on("welcome", (x) => console.log("welcome", x));
      // @ts-ignore catch-all
      socketRef.current.onAny((ev, ...args) => console.log("onAny:", ev, args));

      // If the streaming server is different, create a separate connection
      if (config.STREAMING_SERVER_URL !== config.WEBSOCKET_SERVER_URL) {
        streamingSocketRef.current = io(`${config.STREAMING_SERVER_URL}`, {
          transports: ["websocket"],
        });
      } else {
        streamingSocketRef.current = socketRef.current;
      }

      socketRef.current.on("connect", () => {
        console.log(
          "!!! CONNECTION ESTABLISHED: ",
          socketRef.current.id,
          config.WEBSOCKET_SERVER_URL
        );
        socketRef.current.emit("probe", { t: Date.now() }, (ack) => {
          console.log("!!! PROBE ACK: ", ack);
        });

        console.log(
          "!!! INIT LIVE EVENT: ",
          event_id,
          liveEvent?.broadcast_token
        );
        socketRef.current.emit("init-live-event", {
          token: token,
          event_id: event_id,
          broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
          user_id: activeUser?.public_id, // Replace with actual user ID if needed
        });
      });

      socketRef.current.on("streaming-started", (data) => {
        console.log("!!! HLS LIVE EVENT STARTED: ", data);
        // Handle live event started logic if needed

        if (selectedDisplay === "moderator") {
          //startHLSStream(liveEvent);
          const url = `https://agentwire-streaming-cdn-athfgkebbrhecvb8.z01.azurefd.net/hls/${liveEvent.streaming_token}.m3u8`;
          setTimeout(() => {
            watchHlsSimple(
              url,
              (s) => {
                console.log("!!! HLS status:", s);
                if (s?.error || s?.definiteEnded) {
                  //stopHLSStream();
                  //setShowStreaming(false);
                  //setLiveEventStreaming(false);
                } else {
                  if (!startedHLSStreamRef.current && liveEvent) {
                    console.log("!!! HLS STARTING STREAM");
                    /*setStreamingUrl(url);
                    if (!showStreaming) {
                      console.log("!!! SETTING SHOW STREAMING 2: true");
                      setShowStreaming(true);
                    }
                    */
                    if (!showModeratorVideo) {
                      setShowModeratorVideo(true);
                    }
                    startHLSStream(liveEvent);
                  }
                }
              },
              { intervalMs: 5000, stableForMs: 40000 } // tweak as needed
            );
          }, 6500);
        }
      });

      socketRef.current.on("stream-heartbeat", (data) => {
        // Handle heartbeat if needed
        console.log("!!! STREAM HEARTBEAT");
      });

      socketRef.current.on("streaming-stopped", (data) => {
        console.log("!!! STREAMING STOPPED: ", data);
        // Handle live event stopped logic if needed
        if (selectedDisplay === "moderator") {
          //stopHLSStream();
        }
      });

      socketRef.current.on("live-event-message", (data) => {
        console.log("!!! LIVE EVENT MESSAGE RECEIVED: ", data);
        if (data.event_id === liveEvent?.public_id) {
          const message = {
            user: data.user,
            id: data.public_id,
            public_id: data.public_id,
            message: data.message,
            message_type: data.message_type,
            relevance: data.relevance,
            inappropriate: data.inappropriate,
            relevance_reason: data.relevance_reason,
            inappropriate_reason: data.inappropriate_reason,
            status: data.status || "unreviewed",
            group_id: data.group_id,
          };

          console.log("!!! LIVE EVENT MESSAGE COUNT: ", messages.length);
          const updatedLiveEvent = {
            ...liveEvent,
            messages: [...liveEvent.messages, message],
          };

          setMessages((prevMessages) => [
            ...prevMessages.filter((m) => m.id !== message.id),
            message,
          ]);

          console.log(
            "!!! UPDATED LIVE EVENT MESSAGE COUNT: ",
            updatedLiveEvent.messages.length
          );

          if (message.status !== "dismissed") {
            if (message.message_type === "question") {
              setQuestions((prevQuestions) => [
                ...prevQuestions.filter(
                  (q) => q.public_id !== message.public_id
                ),
                message,
              ]);
            } else {
              setComments((prevComments) => [
                ...prevComments.filter((c) => c.id !== message.id),
                message,
              ]);
            }
          }
          console.log("!!! UPDATING LIVE EVENT: ", updatedLiveEvent);
          setLiveEvent(updatedLiveEvent);
        }
      });

      socketRef.current.on("review-live-event-message", (data) => {
        console.log("!!! REVIEW LIVE EVENT MESSAGE RECEIVED: ", data);
        if (data.event_id === liveEvent?.public_id) {
          const message = {
            id: data.public_id,
            public_id: data.public_id,
            user: data.user,
            message: data.message,
            message_type: data.message_type,
            relevance: data.relevance,
            inappropriate: data.inappropriate,
            relevance_reason: data.relevance_reason,
            inappropriate_reason: data.inappropriate_reason,
            message_id: data.message_id,
          };

          message.waiting_for_review = true;
          const updatedLiveEvent = {
            ...liveEvent,
            messages: [...liveEvent.messages, message],
          };

          setMessages((prevMessages) => [
            ...prevMessages.filter((m) => m.id !== message.id),
            message,
          ]);

          if (message.status !== "dismissed") {
            if (message.message_type === "question") {
              setQuestions((prevQuestions) => [
                ...prevQuestions.filter(
                  (q) => q.public_id !== message.public_id
                ),
                message,
              ]);
            } else {
              setComments((prevComments) => [
                ...prevComments.filter((c) => c.id !== message.id),
                message,
              ]);
            }
          }
          setLiveEvent(updatedLiveEvent);
        }
      });

      socketRef.current.on("event-message", (data) => {
        console.log("!!! SOCKET MESSAGE RECEIVED: ", data);
        // Handle incoming messages from the server if needed
        const question = data.message;
        question.user = data.user;

        setQuestions((prevQuestions) => [
          ...prevQuestions.filter((q) => q.public_id !== question.public_id),
          question,
        ]);
      });

      socketRef.current.on("join-live-event", (data) => {
        console.log("!!! JOIN LIVE EVENT: ", data);
        // Handle joining live event logic if needed
        if (data?.user?.moderator_presenter) {
          return;
        }

        setActiveUsers((prevUsers) => [
          ...prevUsers.filter((user) => user.id !== data.user.id),
          data.user,
        ]);
        setInactiveUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== data.user.id)
        );
      });

      socketRef.current.on("leave-live-event", (data) => {
        console.log("!!! LEAVE LIVE EVENT: ", data);
        // Handle leaving live event logic if needed
        console.log("!!! ACTIVE USERS: ", activeUsers);
        setActiveUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== data.user.id)
        );

        setInactiveUsers((prevUsers) => [
          ...prevUsers.filter((user) => user.id !== data.user.id),
          data.user,
        ]);
      });

      socketRef.current.on("message-status-updated", (data) => {
        console.log("!!! MESSAGE STATUS UPDATED: ", data);

        setMessages((prevMessages) =>
          prevMessages.map((m) =>
            m.public_id === data.public_id
              ? {
                ...m,
                status: data.status,
                message_type: data?.message_type ?? m.message_type,
              }
              : m
          )
        );

        if (data?.message_type === "question") {
          setComments((prevComments) =>
            prevComments.filter((c) => c.public_id !== data.public_id)
          );

          setQuestions((prevQuestions) => {
            // If status is dismissed, remove the question
            if (data.status === "dismissed") {
              return prevQuestions.filter(
                (q) => q.public_id !== data.public_id
              );
            }

            // If the question already exists, update it
            const existing = prevQuestions.find(
              (q) => q.public_id === data.public_id
            );
            if (existing) {
              return prevQuestions.map((q) =>
                q.public_id === data.public_id
                  ? { ...q, status: data.status }
                  : q
              );
            }

            // Otherwise, add it as a new question
            const message = messages.find(
              (m) => m.public_id === data.public_id
            );
            if (message) {
              return [...prevQuestions, { ...message, status: data.status }];
            }

            return prevQuestions;
          });
        } else {
          setQuestions((prevQuestions) =>
            prevQuestions.filter((q) => q.public_id !== data.public_id)
          );

          setComments((prevComments) => {
            // If status is dismissed, remove the comment
            if (data.status === "dismissed") {
              return prevComments.filter((c) => c.public_id !== data.public_id);
            }

            // If the comment already exists, update it
            const existing = prevComments.find(
              (c) => c.public_id === data.public_id
            );
            if (existing) {
              return prevComments.map((c) =>
                c.public_id === data.public_id
                  ? { ...c, status: data.status }
                  : c
              );
            }

            // Otherwise, add it as a new comment
            const message = messages.find(
              (m) => m.public_id === data.public_id
            );
            if (message) {
              return [...prevComments, { ...message, status: data.status }];
            }

            return prevComments;
          });
        }
      });

      socketRef.current.on("message-response-updated", (data) => {
        console.log("!!! MESSAGE RESPONSE UPDATED: ", data);
        const { public_id, response, responded_at, response_by, status } = data;

        setQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q.public_id === public_id
              ? {
                ...q,
                status: status,
                response: response,
                response_by: response_by,
                responded_at: responded_at,
              }
              : q
          )
        );
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEvent]);

  useEffect(() => {
    return () => {
      console.log("!!! SOCKET DISCONNECTING");
      if (socketRef.current) {
        socketRef.current.emit("close-live-event", {
          event_id: event_id,
          broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
          token: token,
        });
        socketRef.current.disconnect();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
      }
      console.log("!!! SOCKET DISCONNECTED");
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("!!! LIVE EVENT DETAILS: ", event_id);
    const loadLiveEventDetails = async () => {
      // Fetch live event details from the server or context
      const liveEvent = await getLiveEvent(event_id);
      console.log("!!! LIVE EVENT DETAILS FETCHED: ", liveEvent);
      if (liveEvent.event_type === "scheduled") {
        setSelectedDisplay("presenter and moderator");
        if (liveEvent.video_url && videoElementRef.current) {
          videoElementRef.current.src = liveEvent.video_url;
          videoElementRef.current.autoplay = false;
        }
      }

      // All messages
      const _messages = [];
      liveEvent?.messages.forEach((data) => {
        const message = {
          user: data.user,
          id: data.public_id,
          public_id: data.public_id,
          message: data.message,
          message_type: data.message_type,
          relevance: data.relevance,
          inappropriate: data.inappropriate,
          relevance_reason: data.relevance_reason,
          inappropriate_reason: data.inappropriate_reason,
          status: data.status || "unreviewed",
        };

        if (message.status !== "dismissed") {
          _messages.push(message);
        }
      });
      setMessages(liveEvent?.messages || []);

      setQuestions(
        liveEvent?.messages.filter((m) => m.message_type === "question") || []
      );
      setComments(
        liveEvent?.messages.filter((m) => m.message_type !== "question") || []
      );

      if (selectedDisplay === "moderator") {
        if (
          liveEvent?.status === "streaming" &&
          liveEvent?.status !== streamingStatusRef.current
        ) {
          streamingStatusRef.current = liveEvent?.status;
          startHLSStream(liveEvent);
        } else if (
          liveEvent?.status !== "streaming" &&
          liveEvent?.status !== streamingStatusRef.current
        ) {
          stopHLSStream();
        }
      }

      console.log(
        "!!! ACTIVE USER ID: ",
        activeUser?.id,
        activeUser?.public_id
      );

      console.log("!!! LIVE EVENT USERS: ", liveEvent?.users);
      const activeUsers =
        liveEvent?.users?.active.filter(
          (user) =>
            user.public_id !== activeUser?.public_id &&
            !user.moderator_presenter &&
            user.status === "active"
        ) ?? [];
      const inactiveUsers =
        liveEvent?.users?.inactive.filter(
          (user) =>
            user.public_id !== activeUser?.public_id &&
            !user.moderator_presenter &&
            user.status !== "active"
        ) ?? [];

      console.log("!!! INACTIVE USERS: ", inactiveUsers);

      setActiveUsers(activeUsers);
      setInactiveUsers(inactiveUsers);
      setLiveEvent(liveEvent);
    };

    if (event_id) {
      loadLiveEventDetails();
    }

    return () => {
      /*if (loadEventInterval) {
        clearInterval(loadEventInterval);
      }*/
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event_id]);

  const _tried = new WeakSet();
  const triedPlayRefGlobal = (v) => {
    return _tried.has(v);
  };
  const markTriedPlay = (v) => {
    _tried.add(v);
  };

  const maybeAutoplay = (video, hls) => {
    // avoid multiple .play() calls racing on some browsers
    if (video.__autoplaying) return;
    video.__autoplaying = true;

    const liveEdge = hls.liveSyncPosition || 0;
    console.log("!!! LIVE EDGE: ", liveEdge);
    if (liveEdge) {
      video.currentTime = liveEdge;
    }

    const tryPlay = () =>
      video.play().catch((ex) => {
        // If autoplay is blocked, unmute won’t help; keep muted true for policy.
        // You can surface a “Tap to play” UI if desired.
        console.log("!!! AUTOPLAY FAILED: ", ex);
      });

    // Call once immediately; some browsers need a microtask delay
    if (!triedPlayRefGlobal(video)) {
      markTriedPlay(video);
      queueMicrotask(tryPlay);
    } else {
      tryPlay();
    }
  };

  const startHLSStream = (event) => {
    setTimeout(() => {
      if (!videoElementRef.current) {
        console.log("!!! VIDEO ELEMENT NOT READY, RETRYING...");
        window.setTimeout(() => startHLSStream(event), 500);
        return;
      }

      startedHLSStreamRef.current = true;
      console.log("!!! VIDEO ELEMENT READY, STARTING HLS STREAM...");

      const videoUrl =
        liveEvent?.archiveUrl ||
        `https://agentwire-streaming-cdn-athfgkebbrhecvb8.z01.azurefd.net/hls/${event.streaming_token}.m3u8`;

      console.log("!!! VIDEO URL: ", videoUrl);
      // eslint-disable-next-line
      if (Hls.isSupported()) {
        console.log("!!! PLAYING HLS WITH HLS.JS");
        let endDetected = false;

        console.log("!!! CAN PLAY HLS WITH HLS.JS");
        // eslint-disable-next-line no-undef
        const hls = new Hls();
        hls.attachMedia(videoElementRef.current);

        // eslint-disable-next-line no-undef
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(videoUrl);
        });

        // eslint-disable-next-line no-undef
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          maybeAutoplay(videoElementRef.current, hls);
        });

        // eslint-disable-next-line
        hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
          console.log("!!! LEVEL LOADED: ", data);
          if (
            data.details.live === false &&
            data.details.endSN === data.details.fragments.at(-1)?.sn
          ) {
            console.log("🎬 Stream is VOD (ended).");
            endDetected = true;
          }
        });

        // eslint-disable-next-line
        hls.on(Hls.Events.FRAG_BUFFERED, (_, data) => {
          console.log("!!! FRAG BUFFERED: ", data);
          console.log(
            `Fragment ${data.frag.sn} ready, duration: ${data.frag.duration}s`
          );

          if (
            endDetected &&
            data.frag.sn === hls?.levels?.[hls.currentLevel]?.details?.endSN
          ) {
            // Even though the end is detected, I need to wait until the last fragment is buffered and played before I stop
            videoElementRef.current?.addEventListener("timeupdate", () => {
              const currentTime = videoElementRef.current?.currentTime;
              const duration = videoElementRef.current?.duration;

              if (currentTime && duration && currentTime >= duration) {
                console.log("🎬 Reached end of stream.");
                stopHLSStream();
                setShowModeratorVideo(false);
                //setShowStreaming(false);
                //setLiveEventStreaming(false);
                console.log("✅ Last segment buffered — stream ended.");

                videoElementRef.current?.removeEventListener(
                  "timeupdate",
                  () => {
                    console.log("Timeupdate listener removed.");
                  }
                );
              }
            });
          }
        });
      }
    }, 1000);
  };

  /*
  const startHLSStream = (liveEvent) => {
    setShowModeratorVideo(true);
    setTimeout(() => {
      if (!videoElementRef.current) {
        window.setTimeout(() => startHLSStream(liveEvent), 500);
        return;
      }

      if (
        videoElementRef.current?.canPlayType("application/vnd.apple.mpegurl")
      ) {
        console.log("!!! CAN PLAY HLS NATIVE");
        setStreamingUrl(
          `https://agentwire-streaming-cdn-athfgkebbrhecvb8.z01.azurefd.net/hls/${liveEvent.streaming_token}.m3u8`
        );
        // eslint-disable-next-line no-undef
      } else if (Hls.isSupported()) {
        console.log("!!! CAN PLAY HLS WITH HLS.JS");
        // eslint-disable-next-line no-undef
        const hls = new Hls();
        hls.attachMedia(videoElementRef.current);

        // eslint-disable-next-line no-undef
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(
            `https://agentwire-streaming-cdn-athfgkebbrhecvb8.z01.azurefd.net/hls/${liveEvent.streaming_token}.m3u8`
          );
        });

        // eslint-disable-next-line no-undef
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          maybeAutoplay(videoElementRef.current, hls);
        });
      }

    }, 1000);
  };
  */

  const stopHLSStream = () => {
    startedHLSStreamRef.current = false;
    console.log("!!! STOP HLS STREAM");
    if (videoElementRef.current?.tagName !== "VIDEO") {
      return;
    }
    if (videoElementRef.current?.canPlayType("application/vnd.apple.mpegurl")) {
      console.log("!!! CAN PLAY HLS NATIVE");
      // eslint-disable-next-line no-undef
    } else if (Hls.isSupported()) {
      console.log("!!! CAN PLAY HLS WITH HLS.JS");
      // eslint-disable-next-line no-undef
      const hls = new Hls();
      hls.destroy();
    }
  };

  const saveVideo = async () => {
    // Turn media chunks into a base64 video
    setSavingVideo(true);

    if (!recordedBlob.current && liveEvent.video_url !== selectedVideoURL) {
      const updatedLiveEvent = { ...liveEvent };
      updatedLiveEvent.video_url = selectedVideoURL;
      updatedLiveEvent.timezone = getFullTimeZone();
      await saveLiveEvent(updatedLiveEvent);
      setLiveEvent(updatedLiveEvent);
      setSavingVideo(false);
      return;
    }

    const base64 = await blobToBase64(recordedBlob.current);
    console.log("!!! BASE64: ", base64);
    const results = await saveVideoToLiveEvent(
      token,
      liveEvent.public_id,
      base64
    );

    console.log("!!! RESULTS:  ", results);
    if (results.success) {
      const updatedLiveEvent = { ...liveEvent };
      updatedLiveEvent.video_url = results.video_url;
      const scheduled_videos = updatedLiveEvent.scheduled_videos;
      scheduled_videos.push({
        video_url: results.video_url,
        title: results.video_file_name,
      });

      setLiveEvent(updatedLiveEvent);
      videoElementRef.current.src = results.video_url;

      const options = [...scheduledVideoOptions];
      options.push({
        value: results.video_url,
        label: results.video_file_name,
      });
      console.log("!!! NEW OPTIONS: ", options);
      //setScheduledVideoOptions(options);
      setSelectedVideoURL(results.video_url);
    }
    setSavingVideo(false);
    setRecording(false);
    setRecordingComplete(false);
  };

  const startRecording = async () => {
    setRecording(true);
    mediaChunks.current = [];
    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
    } catch (error) {
      console.error("Error accessing media devices.", error);
    }

    setRecordingComplete(false);
    videoElementRef.current.autoplay = true;
    videoElementRef.current.muted = true; // Mute the video element to avoid feedback
    videoElementRef.current.volume = 0;
    videoElementRef.current.srcObject = mediaStreamRef.current;
    console.log("!!! GOT MEDIA STREAM: ", mediaStreamRef.current);
    console.log("!!! STREAMING WITH MIME TYPE: ", mimeType);

    mediaRecorderRef.current = new MediaRecorder(mediaStreamRef.current, {
      mimeType: mimeType,
    });

    mediaRecorderRef.current.start(500); // Start recording with a 1 second interval

    console.log("!!! MEDIA RECORDER: ", mediaRecorderRef.current);

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event?.data.size > 0) {
        mediaChunks.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = async () => {
      recordedBlob.current = new Blob(mediaChunks.current, {
        type: mediaRecorderRef?.current.mimeType || "video/webm",
      });
      const videoURL = URL.createObjectURL(recordedBlob.current);
      console.log("!!! VIDEO URL: ", videoURL);
      videoElementRef.current.srcObject = null;
      videoElementRef.current.src = videoURL;
      setTimeout(() => {
        videoElementRef.current.play();
      }, 2000);
      setRecordingComplete(true);
    };
  };

  const startLiveEvent = async () => {
    const broadcastToken = liveEvent?.broadcast_token;
    setRecording(true);
    setTranscript("");
    console.log("!!! START LIVE EVENT: ", event_id);
    console.log("!!! START STREAMING TOKEN: ", broadcastToken);

    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
    } catch (error) {
      console.error("Error accessing media devices.", error);
    }

    videoElementRef.current.muted = true; // Mute the video element to avoid feedback
    videoElementRef.current.volume = 0;
    videoElementRef.current.srcObject = mediaStreamRef.current;
    console.log("!!! GOT MEDIA STREAM: ", mediaStreamRef.current);
    console.log("!!! STREAMING WITH MIME TYPE: ", mimeType);

    mediaRecorderRef.current = new MediaRecorder(mediaStreamRef.current, {
      mimeType: mimeType,
    });

    mediaRecorderRef.current.start(500); // Start recording with a 1 second interval

    console.log("!!! MEDIA RECORDER: ", mediaRecorderRef.current);

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        event.data.arrayBuffer().then((buffer) => {
          console.log("!!! DATA AVAILABLE SIZE: ", buffer.byteLength);
          streamingSocketRef.current.emit("stream", {
            broadcast_token: broadcastToken,
            buffer: new Uint8Array(buffer),
            event_id: event_id,
            token: token,
          });
        });
      }
    };

    streamingSocketRef.current.emit("start-streaming", {
      event_id: event_id,
      broadcast_token: broadcastToken,
      token: token,
    });
  };

  const hasEventEnded = () => {
    return liveEvent?.status === "ended" || liveEvent?.completed_at;
  };

  const stopLiveEvent = () => {
    console.log("!!! STOP LIVE EVENT: ", event_id);
    setTranscript("");
    recognitionRef.current?.stop();

    streamingSocketRef.current.emit("stop-streaming", {
      event_id: event_id,
      token: token,
    });
    setRecording(false);
    videoElementRef.current.srcObject = null;
    mediaStreamRef.current.getTracks().forEach((track) => {
      track.stop();
    });
    mediaRecorderRef.current.stop();

    mediaRecorderRef.current.ondataavailable = null;
  };

  const endLiveEvent = async () => {
    if (recording) {
      stopLiveEvent();
    }

    await endLiveEventOnServer(token, event_id);

    socketRef.current.emit("end-live-event", {
      event_id: event_id,
      broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
      token: token,
    });

    const updatedLiveEvent = {
      ...liveEvent,
      status: "ended",
    };
    setLiveEvent(updatedLiveEvent);
  };

  const updateMessage = (updatedMessage) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === updatedMessage.id ? updatedMessage : msg
      )
    );

    if (updatedMessage.message_type === "question") {
      console.log("!!! UPDATE QUESTION: ", updatedMessage, questions);
      setQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q.public_id === updatedMessage.public_id ? updatedMessage : q
        )
      );
    } else {
      setComments((prevComments) =>
        prevComments.map((c) =>
          c.public_id === updatedMessage.public_id ? updatedMessage : c
        )
      );
    }
  };

  if (!liveEvent) {
    return <Loading>Live Event Loading...</Loading>;
  }

  return (
    <div className="live-event-details" style={{ marginTop: "-20px", height: "95vh" }}>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "calc(100% - 600px)",
            height: "95vh",
            maxWidth: "1200px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: "65px",
              color: "var(--main-font-color)"
            }}
          >
            <div style={{ width: "55%" }}>
              <div
                style={{
                  fontSize: "26px",
                  fontWeight: "600",
                  lineHeight: "26px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {liveEvent?.event_type === "scheduled" ? "Scheduled" : "Live"}{" "}
                Broadcast{" "}
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "400",
                    marginLeft: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    NiceModal.show("edit-live-event", {
                      liveEvent: liveEvent,
                    }).then((updatedLiveEvent) => {
                      const newLiveEvent = { ...liveEvent };
                      Object.entries(updatedLiveEvent).forEach((k, v) => {
                        newLiveEvent[k] = v;
                      });
                      setLiveEvent(newLiveEvent);

                      if (newLiveEvent.event_type === "scheduled") {
                        setSelectedVideoURL(newLiveEvent.video_url);
                      }
                    });
                  }}
                >
                  <i className="fas fa-edit"></i>
                </span>
                {liveEvent?.event_type !== "scheduled" ? (
                  <>
                    {hasEventEnded() ? (
                      <span
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          marginLeft: "10px",
                          cursor: "pointer",
                          // Light red background
                          padding: "0px 8px",
                          borderRadius: "5px",
                          color: "#f87171",
                        }}
                      >
                        <i className="fas fa-check"></i>&nbsp;&nbsp;Event Ended
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "400",
                          marginLeft: "10px",
                          cursor: "pointer",
                          // Light red background
                          padding: "0px 8px",
                          borderRadius: "5px",
                          color: "#fff",
                          backgroundColor: "#f87171",
                        }}
                        onClick={() => {
                          Swal.fire({
                            title: "End Live Event",
                            text:
                              "Are you sure you want to end this live event?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#f87171",
                            cancelButtonColor: "#3085d6",
                            confirmButtonText: "Yes, end it!",
                            cancelButtonText: "No, cancel!",
                          }).then((result) => {
                            console.log("!!! ENDING LIVE EVENT: ", result);
                            if (result?.value) {
                              endLiveEvent();
                              Swal.fire(
                                "Ended!",
                                "The live event has been ended.",
                                "success"
                              );
                            } else {
                              console.log("!!! LIVE EVENT ENDING CANCELLED");
                            }
                          });
                        }}
                      >
                        <i className="fas fa-check"></i>&nbsp;&nbsp;End Event
                      </span>
                    )}
                  </>
                ) : null}
              </div>
              <div style={{ fontSize: "18px", fontWeight: "400" }}>
                {liveEvent?.title ?? "Loading..."}:{" "}
                {liveEvent?.description ?? ""}
              </div>
            </div>
            <div
              style={{
                width: "45%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
              }}
            >
              {liveEvent?.event_type !== "scheduled" ? (
                <div style={{ marginRight: "20px" }}>
                  <Select
                    name="display"
                    value={displayOptions.find(
                      (option) => option.value === selectedDisplay
                    )}
                    options={displayOptions}
                    onChange={(option) => setSelectedDisplay(option.value)}
                  />
                </div>
              ) : null}
              {!hasEventEnded() && selectedDisplay.includes("presenter") ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {!recording ? (
                    <button
                      className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                      style={{ width: "220px" }}
                      onClick={() => {
                        if (liveEvent.event_type === "scheduled") {
                          startRecording();
                        } else {
                          startLiveEvent();
                        }
                      }}
                    >
                      <div
                        style={{
                          flexDirection: "row",
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#fff",
                            marginRight: "10px",
                          }}
                        >
                          <div
                            className="bg-emerald-500"
                            style={{
                              width: "10px",
                              height: "10px",
                              margin: "5px auto",
                              borderRadius: "50%",
                            }}
                          ></div>
                        </div>
                        <div>
                          {liveEvent.event_type === "scheduled"
                            ? "Start Recording"
                            : "Start Streaming"}
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      className="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                      onClick={() => {
                        stopLiveEvent();
                      }}
                      style={{ width: "220px" }}
                    >
                      <div
                        style={{
                          flexDirection: "row",
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#fff",
                            marginRight: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              backgroundColor: "#ff0000",
                              margin: "5px auto",
                            }}
                          ></div>
                        </div>
                        <div>
                          Stop{" "}
                          {liveEvent.event_type === "scheduled"
                            ? "Recording"
                            : "Streaming"}
                        </div>
                      </div>
                    </button>
                  )}
                  {liveEvent.event_type === "scheduled" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <Select
                        name="videoURL"
                        value={scheduledVideoOptions.find(
                          (option) => option.value === selectedVideoURL
                        )}
                        options={scheduledVideoOptions}
                        onChange={(option) => {
                          console.log("!!! OPTION VALUE: ", option.value);
                          setSelectedVideoURL(option.value);
                        }}
                        style={{ width: "200px" }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <div
            style={{
              borderRadius: "10px",
              marginTop: "10px",
              aspectRatio: "16/9",
              position: "relative",
            }}
          >
            {selectedDisplay.includes("presenter") ? (
              <>
                <video
                  ref={videoElementRef}
                  id="liveVideoRecorder"
                  autoPlay
                  muted
                  controls
                  style={{
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    position: "absolute",
                    objectFit: "cover",
                  }}
                  onCanPlay={() => {
                    console.log("Video can play");
                  }}
                ></video>
                {liveEvent.event_type === "scheduled" &&
                  (recordingComplete ||
                    liveEvent.video_url !== selectedVideoURL) ? (
                  <div
                    style={{ position: "absolute", top: "20px", right: "20px" }}
                  >
                    <button
                      className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                      style={{ width: "220px" }}
                      onClick={() => {
                        saveVideo();
                      }}
                    >
                      {savingVideo ? "Saving Video" : "Save Video"}
                    </button>
                  </div>
                ) : null}
                <div
                  style={{
                    position: "absolute",
                    bottom: "20px",
                    left: "10px",
                    width: "100%",
                    fontSize: "26px",
                    color: "white",
                    textAlign: "center",
                    textShadow: "2px 2px 4px #000000",
                  }}
                >
                  {transcript}
                </div>
              </>
            ) : (
              <div>
                {showModeratorVideo ? (
                  <video
                    ref={videoElementRef}
                    id="liveVideoStream"
                    src={""}
                    autoPlay
                    muted
                    controls
                    style={{
                      width: "100%",
                      height: "100%",
                      top: 0,
                      left: 0,
                      position: "absolute",
                      objectFit: "cover",
                    }}
                    onCanPlay={() => {
                      console.log("Video can play");
                    }}
                  ></video>
                ) : (
                  <div
                    ref={videoElementRef}
                    style={{
                      width: "100%",
                      height: "100%",
                      top: 0,
                      left: 0,
                      position: "absolute",
                      objectFit: "cover",
                      backgroundColor: "black",
                      color: "white",
                      fontSize: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    Presenter is not broadcasting
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedDisplay.includes("moderator") ? (
            <div>
              <TabBar
                tabs={["Incoming", "Answered", "Unreviewed", "Flagged"]}
                counts={[
                  incomingCount,
                  answeredCount,
                  unreviewedCount + unreviewedCommentsCount,
                  flaggedCount + flaggedCommentsCount,
                ]}
                selectedTab={selectedBottomBar}
                onSelect={(tab) => setSelectedBottomBar(tab)}
              />
              <Incoming
                display={selectedBottomBar === "Incoming"}
                height={bottomHeight}
                questions={questions}
                liveEvent={liveEvent}
                socketRef={socketRef}
                setQuestions={setQuestions}
              />
              <Answered
                display={selectedBottomBar === "Answered"}
                height={bottomHeight}
                questions={questions}
                liveEvent={liveEvent}
                socketRef={socketRef}
                setQuestions={setQuestions}
              />
              <Unreviewed
                display={selectedBottomBar === "Unreviewed"}
                height={bottomHeight}
                questions={questions}
                comments={comments}
                liveEvent={liveEvent}
                socketRef={socketRef}
                setQuestions={setQuestions}
                setComments={setComments}
                updateMessage={updateMessage}
              />
              <Flagged
                display={selectedBottomBar === "Flagged"}
                height={bottomHeight}
                questions={questions}
                comments={comments}
                liveEvent={liveEvent}
                socketRef={socketRef}
                setQuestions={setQuestions}
                setComments={setComments}
                updateMessage={updateMessage}
              />
            </div>
          ) : null}
          <div
            style={{
              display:
                false && selectedBottomBar === "Incoming" ? "block" : "none",
              paddingTop: "20px",
            }}
          >
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                marginTop: "40px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                Approved Questions
              </div>
              <div
                style={{
                  backgroundColor: "#e0f2fe",
                  color: "#0284c7",
                  width: "50px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  textAlign: "center",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-question-circle"
                  style={{ fontSize: "10px", marginRight: "5px" }}
                ></i>{" "}
                {approvedCount}
              </div>
            </div>
            <ul>
              {approvedCount > 0 ? (
                questions
                  .filter((q) => q.status === "approved")
                  .map((question, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: "16px",
                        marginBottom: "10px",
                        width: "100%",
                        backgroundColor: "#f0f6ff",
                        border: "1px solid #d0d6dd",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        padding: "5px 10px",
                        flexDirection: "column",
                      }}
                    >
                      <div>{question?.message}</div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        From: {question?.user?.firstname}{" "}
                        {question?.user?.lastname}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          marginTop: "5px",
                          fontSize: "14px",
                          justifyContent: "space-around",
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          bgcolor="#89abf5"
                          textcolor="#f0f6ff"
                          onClick={() => {
                            console.log("!!! DISMISSING QUESTION: ", question);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: question.public_id,
                              status: "dismissed",
                              token: token,
                            });
                            setQuestions((prevQuestions) =>
                              prevQuestions.filter(
                                (q) => q.public_id !== question.public_id
                              )
                            );
                          }}
                        >
                          <i className="fas fa-times"></i>
                          Dismiss
                        </Button>
                        <Button
                          bgcolor="#89abf5"
                          textcolor="#f0f6ff"
                          onClick={() => {
                            console.log("!!! ANSWERING QUESTION: ", question);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: question.public_id,
                              status: "answered",
                              token: token,
                            });

                            setQuestions((prevQuestions) =>
                              prevQuestions.map((q) =>
                                q.public_id === question.public_id
                                  ? { ...q, status: "answered" }
                                  : q
                              )
                            );
                          }}
                        >
                          <i className="fas fa-check "></i>
                          Mark as Answered
                        </Button>
                      </div>
                    </li>
                  ))
              ) : (
                <li>No questions available.</li>
              )}
            </ul>
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                marginTop: "40px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                Incoming Questions (&gt; {liveEvent?.relevance_threshold ?? 0.5}
                )
              </div>
              <div
                style={{
                  backgroundColor: "#fef9c3",
                  color: "#b45309",
                  width: "50px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  textAlign: "center",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-question-circle"
                  style={{ fontSize: "10px", marginRight: "5px" }}
                ></i>{" "}
                {incomingCount}
              </div>
            </div>
            <ul>
              {incomingCount > 0 ? (
                questions
                  .filter(
                    (q) =>
                      !q.inappropriate &&
                      (!q.status || q.status === "pending") &&
                      q.relevance >= (liveEvent?.relevance_threshold || 0.5)
                  )
                  .sort((a, b) => b.relevance - a.relevance)
                  .map((question, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: "16px",
                        marginBottom: "10px",
                        width: "100%",
                        border: "1px solid #d0d0d0",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        padding: "5px 10px",
                        flexDirection: "column",
                      }}
                    >
                      <div>{question?.message}</div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        From: {question?.user.firstname}{" "}
                        {question?.user.lastname}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        Relevance: {question?.relevance} (
                        {question?.relevance_reason})
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          marginTop: "5px",
                          fontSize: "14px",
                          justifyContent: "space-around",
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          bgcolor="#f3f4f6"
                          textcolor="#334155"
                          onClick={() => {
                            console.log("!!! DISMISSING QUESTION: ", question);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: question.public_id,
                              status: "dismissed",
                              token: token,
                            });
                            setQuestions((prevQuestions) =>
                              prevQuestions.filter(
                                (q) => q.public_id !== question.public_id
                              )
                            );
                          }}
                        >
                          <i className="fas fa-times"></i>
                          Dismiss
                        </Button>
                        <Button
                          bgcolor="#f3f4f6"
                          textcolor="#334155"
                          onClick={(e) => {
                            console.log("EVENT ID: ", event_id);
                            console.log("!!! REPLYING TO QUESTION: ", question);
                            NiceModal.show("answer-modal", {
                              event_id: event_id,
                              question: question,
                              position: {
                                top: e.clientY + 150,
                                left: e.clientX - 150,
                              },
                            }).then((data) => {
                              const { response, source } = data;
                              question.response = response;
                              question.responded_at = new Date().toISOString();

                              if (source !== "agent") {
                                question.response_by = `${activeUser?.firstname} ${activeUser?.lastname}`; // Replace with actual user ID if needed
                              } else {
                                question.response_by = "agent";
                              }
                              question.response_source = source;
                              question.status = "answered";

                              console.log(
                                "!!! QUESTION BEFORE UPDATE: ",
                                question
                              );
                              setQuestions((prevQuestions) =>
                                prevQuestions.map((q) =>
                                  q.public_id === question.public_id
                                    ? {
                                      ...q,
                                      status: "answered",
                                      response: response,
                                      response_source: source,
                                      responded_at: new Date().toISOString(),
                                      response_by: {
                                        firstname:
                                          source === "agent"
                                            ? "agent"
                                            : activeUser?.firstname,
                                        lastname:
                                          source === "agent"
                                            ? ""
                                            : activeUser?.lastname,
                                        email:
                                          source === "agent"
                                            ? ""
                                            : activeUser?.email,
                                      },
                                    }
                                    : q
                                )
                              );

                              console.log("!!! SOURCE: ", source);
                              socketRef.current.emit(
                                "update-message-response",
                                {
                                  event_id: event_id,
                                  broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                                  message_id: question.public_id,
                                  status: "answered",
                                  response: response,
                                  response_by: activeUser?.public_id, // Replace with actual user ID if needed
                                  response_source: source,
                                  token: token,
                                }
                              );
                            });
                          }}
                        >
                          <i className="fas fa-reply"></i>
                          Answer
                        </Button>
                        <Button
                          bgcolor="#dcfce8"
                          textcolor="#16a34a"
                          onClick={() => {
                            console.log("!!! APPROVING QUESTION: ", question);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: question.public_id,
                              status: "approved",
                              token: token,
                            });
                            setQuestions((prevQuestions) =>
                              prevQuestions.map((q) =>
                                q.public_id === question.public_id
                                  ? { ...q, status: "approved" }
                                  : q
                              )
                            );
                          }}
                        >
                          <i className="fas fa-check-circle"></i>
                          Approve
                        </Button>
                      </div>
                    </li>
                  ))
              ) : (
                <li>No questions available.</li>
              )}
            </ul>
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                marginTop: "40px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                Answered Questions
              </div>
              <div
                style={{
                  backgroundColor: "#dcfce8",
                  color: "#16a34a",
                  width: "50px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  textAlign: "center",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-question-circle"
                  style={{ fontSize: "10px", marginRight: "5px" }}
                ></i>{" "}
                {answeredCount}
              </div>
            </div>
            <ul>
              {answeredCount > 0 ? (
                questions
                  .filter((q) => q.status === "answered" || q.response)
                  .map((question, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: "16px",
                        marginBottom: "10px",
                        width: "100%",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        padding: "5px 10px",
                        flexDirection: "column",
                        backgroundColor: "#dcfce8",
                        borderColor: "#16a34a",
                      }}
                    >
                      <div>{question?.message}</div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        From: {question?.user?.firstname}{" "}
                        {question?.user?.lastname}
                      </div>
                      <div
                        style={{
                          backgroundColor: "white",
                          width: "100%",
                          borderRadius: "10px",
                          padding: "5px 10px",
                          marginTop: "10px",
                          marginBottom: "10px",
                          fontSize: "14px",
                        }}
                      >
                        {question?.response}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          fontSize: "14px",
                          color: "#808080",
                          justifyContent: "space-between",
                          width: "100%",
                          fontStyle: "italic",
                        }}
                      >
                        Answered By:{" "}
                        {question.response_by
                          ? `${question.response_by?.firstname} ${question.response_by?.lastname}`
                          : ""}
                      </div>
                    </li>
                  ))
              ) : (
                <li>No questions answered.</li>
              )}
            </ul>
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                marginTop: "40px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                Flagged Questions
              </div>
              <div
                style={{
                  backgroundColor: "#ffe5e5",
                  color: "#ff0000",
                  width: "50px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  textAlign: "center",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-question-circle"
                  style={{ fontSize: "10px", marginRight: "5px" }}
                ></i>{" "}
                {flaggedCount}
              </div>
            </div>
            <ul>
              {flaggedCount > 0 ? (
                questions
                  .filter((q) => q.inappropriate)
                  .sort((a, b) => b.relevance - a.relevance)
                  .map((question, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: "16px",
                        marginBottom: "10px",
                        width: "100%",
                        border: "1px solid #d0d0d0",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        padding: "5px 10px",
                        flexDirection: "column",
                        // Light red
                        backgroundColor: "#ffe5e5",
                        borderColor: "#ff0000",
                      }}
                    >
                      <div>{question?.message}</div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        From: {question?.user.firstname}{" "}
                        {question?.user.lastname}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        Marked Inappropriate: {question?.inappropriate_reason}{" "}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          marginTop: "5px",
                          fontSize: "14px",
                          justifyContent: "space-around",
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          bgcolor="#f3f4f6"
                          textcolor="#334155"
                          onClick={() => {
                            console.log("!!! DISMISSING QUESTION: ", question);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: question.public_id,
                              status: "dismissed",
                              token: token,
                            });
                            setQuestions((prevQuestions) =>
                              prevQuestions.filter(
                                (q) => q.public_id !== question.public_id
                              )
                            );
                          }}
                        >
                          <i className="fas fa-times"></i>
                          Dismiss
                        </Button>
                        <Button
                          bgcolor="#f3f4f6"
                          textcolor="#334155"
                          onClick={() => {
                            console.log("!!! APPROVING QUESTION: ", question);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: question.public_id,
                              inappropriate: false,
                              status: "approved",
                              token: token,
                            });

                            setQuestions((prevQuestions) =>
                              prevQuestions.map((q) =>
                                q.public_id === question.public_id
                                  ? {
                                    ...q,
                                    status: "manually_approved",
                                    inappropriate: false,
                                  }
                                  : q
                              )
                            );
                          }}
                        >
                          <i className="fas fa-reply"></i>
                          Accept
                        </Button>
                      </div>
                    </li>
                  ))
              ) : (
                <li>No questions available.</li>
              )}
            </ul>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            minWidth: "330px",
            marginLeft: "20px",
          }}
        >
          <TabBar
            tabs={["Approved"]}
            selectedTab={"Approved"}
            counts={[approvedCount]}
          />
          <Approved
            display={true}
            questions={questions.filter(
              (q) =>
                q.status === "approved" ||
                q.status === "answered" ||
                q.response !== null
            )}
            socketRef={socketRef}
            liveEvent={liveEvent}
            setQuestions={setQuestions}
          />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: "330px",
            marginLeft: "20px",
            color: "var(--main-font-color)",
          }}
        >
          <TabBar
            tabs={["Chat", "Audience"]}
            selectedTab={selectedSidebar}
            onSelect={(tab) => setSelectedSidebar(tab)}
            counts={[null, activeUsers.length]}
          />
          <Audience
            height={sidebarBottomHeight}
            display={selectedSidebar === "Audience"}
            socketRef={socketRef}
            liveEvent={liveEvent}
            activeUsers={activeUsers}
            inactiveUsers={inactiveUsers}
          />
          <LiveEventChatSession
            display={selectedSidebar === "Chat"}
            height={sidebarBottomHeight}
            ended={hasEventEnded()}
            messages={
              messages.filter(
                (m) =>
                  m.status !== "unreviewed" &&
                  !m.inappropriate &&
                  !m.waiting_for_review
              ) ?? []
            }
            relevanceThreshold={liveEvent?.relevance_threshold || 0.5}
            event_id={liveEvent?.public_id}
            user_id={activeUser?.public_id}
            ws={socketRef}
            promoteContent={(message_id, relevance) => {
              socketRef.current.emit("promote-comment", {
                event_id: event_id,
                broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                message_id: message_id,
                token: token,
                relevance:
                  relevance > (liveEvent?.relevance_threshold || 0.5)
                    ? relevance
                    : liveEvent?.relevance_threshold || 0.5,
              });

              setMessages((prevMessages) =>
                prevMessages.map((msg) =>
                  msg.public_id === message_id
                    ? {
                      ...msg,
                      message_type: "question",
                      status: "approved",
                      relevance: liveEvent?.relevance_threshold || 0.5,
                    }
                    : msg
                )
              );

              const message = messages.find((m) => m.public_id === message_id);

              message.message_type = "question";
              message.status = "approved";

              setQuestions((prevQuestions) => [...prevQuestions, message]);

              setComments((prevComments) =>
                prevComments.filter((c) => c.public_id !== message_id)
              );
            }}
          />
          <div
            style={{
              display: selectedSidebar === "Comments" ? "block" : "none",
              paddingTop: "20px",
            }}
          >
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                marginTop: "40px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600" }}>
                Flagged Comments
              </div>
              <div
                style={{
                  backgroundColor: "#ffe5e5",
                  color: "#ff0000",
                  width: "50px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  textAlign: "center",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-question-circle"
                  style={{ fontSize: "10px", marginRight: "5px" }}
                ></i>{" "}
                {flaggedCommentsCount}
              </div>
            </div>
            <ul>
              {flaggedCommentsCount > 0 ? (
                comments
                  .filter((q) => q.inappropriate)
                  .sort((a, b) => b.relevance - a.relevance)
                  .map((comment, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: "16px",
                        marginBottom: "10px",
                        width: "100%",
                        border: "1px solid #d0d0d0",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        padding: "5px 10px",
                        flexDirection: "column",
                        // Light red
                        backgroundColor: "#ffe5e5",
                        borderColor: "#ff0000",
                      }}
                    >
                      <div>{comment?.message}</div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        From: {comment?.user.firstname} {comment?.user.lastname}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#808080",
                          fontStyle: "italic",
                        }}
                      >
                        Marked Inappropriate: {comment?.inappropriate_reason}{" "}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          marginTop: "5px",
                          fontSize: "14px",
                          justifyContent: "space-around",
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          bgcolor="#f3f4f6"
                          textcolor="#334155"
                          onClick={() => {
                            console.log("!!! DISMISSING comment: ", comment);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: comment.public_id,
                              status: "dismissed",
                              token: token,
                            });
                            setComments((prevcomments) =>
                              prevcomments.filter((q) => q.id !== comment.id)
                            );
                          }}
                        >
                          <i className="fas fa-times"></i>
                          Reject
                        </Button>
                        <Button
                          bgcolor="#f3f4f6"
                          textcolor="#334155"
                          onClick={(e) => {
                            console.log("!!! REPLYING TO comment: ", comment);
                            socketRef.current.emit("update-message-status", {
                              event_id: event_id,
                              broadcast_token: liveEvent?.broadcast_token, // Replace with actual token if needed
                              message_id: comment.public_id,
                              inappropriate: false,
                              status: "manually_approved",
                              token: token,
                            });

                            setComments((prevcomments) =>
                              prevcomments.map((q) =>
                                q.id === comment.id
                                  ? {
                                    ...q,
                                    status: "manually_approved",
                                    inappropriate: false,
                                  }
                                  : q
                              )
                            );
                          }}
                        >
                          <i className="fas fa-reply"></i>
                          Accept
                        </Button>
                      </div>
                    </li>
                  ))
              ) : (
                <li>No flagged comments.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveEventDetails;
